import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

const LandingPage = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);

  const tiers = [
    {
      key: "starter",
      name: "Starter",
      monthlyPrice: 50,
      annualPrice: 500,
      description: "Perfect for new entrepreneurs",
      badge: null,
      features: [
        "1 business",
        "5 Opturna accounts",
        "Basic allocation tracking",
        "Email reminders",
        "Deductions library",
        "Basic reports"
      ],
      highlighted: false
    },
    {
      key: "pro",
      name: "Pro",
      monthlyPrice: 99,
      annualPrice: 990,
      description: "For growing businesses ready to scale",
      badge: "Most Popular",
      features: [
        "3 businesses",
        "5 Opturna accounts",
        "Bank connections (Plaid)",
        "PDF & CSV exports",
        "Tax planning tools",
        "Email & SMS reminders",
        "CPA email reports",
        "Financial health score"
      ],
      highlighted: true
    },
    {
      key: "enterprise",
      name: "Enterprise",
      monthlyPrice: 149,
      annualPrice: 1490,
      description: "Full power for established businesses",
      badge: "Best Value",
      features: [
        "Unlimited businesses",
        "5 Opturna accounts",
        "Bank connections (Plaid)",
        "PDF & CSV exports",
        "Tax planning tools",
        "Email & SMS reminders",
        "CPA email reports",
        "Financial health score",
        "Priority support",
        "Custom reports"
      ],
      highlighted: false
    },
    {
      key: "wealth_builder",
      name: "Wealth Builder",
      monthlyPrice: 249,
      annualPrice: 2490,
      description: "Tax App + Wealth Engine for serious builders",
      badge: "Upgrade",
      features: [
        "Everything in Enterprise",
        "Wealth Engine",
        "Available to Deploy calc",
        "Goal tracking & contributions",
        "Allocation suggestions",
        "Insights Engine",
        "Activate This Step actions",
        "Dedicated onboarding"
      ],
      highlighted: false
    }
  ];

  const calculateSavings = (monthly, annual) => {
    return (monthly * 12) - annual;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a1628]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({top:0, behavior:'smooth'})}>
              <img src="/logo_compact.png" alt="Opturna Tax" className="h-7 rounded" data-testid="header-logo" />
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-stone-300 hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="text-sm text-stone-300 hover:text-white transition-colors">Pricing</a>
              <a href="#how-it-works" className="text-sm text-stone-300 hover:text-white transition-colors">How it Works</a>
            </nav>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate("/auth")} className="text-stone-300 hover:text-white hover:bg-white/10" data-testid="login-btn">
                Log in
              </Button>
              <Button onClick={() => navigate("/auth")} className="bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-600 hover:to-teal-500 text-white rounded-full px-6 border-0" data-testid="get-started-btn">
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Bold gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#1e3a5f] to-[#0d4a3a]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvc3ZnPg==')] opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-400/15 text-emerald-300 text-sm font-medium border border-emerald-400/20">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                AI-Powered Tax Intelligence for Your Business
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight">
                Stop Surviving.
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300"> Start Profiting.</span>
              </h1>
              <p className="text-lg sm:text-xl text-stone-300 leading-relaxed max-w-lg">
                Opturna Tax&#8482; is your all-in-one business financial command center — built to help you keep more of what you earn.
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 max-w-lg">
                {[
                  "Automated Bookkeeping",
                  "AI-Powered Tax Planning",
                  "Receipt OCR",
                  "Deduction Tracking",
                  "Profit-First Smart Accounts",
                  "Cash Flow Projections",
                  "Quarterly Reviews",
                  "Real-Time AI Financial Assistant"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-stone-300">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate("/auth")}
                  className="bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-600 hover:to-teal-500 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-emerald-500/25 border-0"
                  data-testid="hero-cta-btn"
                >
                  Start 7-Day Free Trial
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg border-white/20 text-white hover:bg-white/10 bg-transparent"
                  onClick={() => navigate("/demo")}
                  data-testid="hero-demo-btn"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  Try Interactive Demo
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-2 flex-wrap">
                {["7-day free trial", "Cancel anytime", "2 months free (annual)"].map((text) => (
                  <div key={text} className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs text-stone-400">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 to-teal-400/20 rounded-3xl blur-3xl"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/30">
                  <img src="/hero-team.png" alt="Business team using Opturna Tax" className="w-full h-[280px] sm:h-[350px] lg:h-[420px] object-cover" />
                </div>
                {/* Dashboard card below photo */}
                <div className="relative -mt-2 mx-2 sm:mx-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 sm:p-4 shadow-xl">
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <div>
                      <p className="text-[10px] text-stone-500 uppercase tracking-wide">Financial Health</p>
                      <p className="text-xl sm:text-2xl font-bold text-[#1e3a5f]">87<span className="text-sm text-stone-400">/100</span></p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700 border-0">Excellent</Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-[#1e3a5f]/5 text-center">
                      <p className="text-[8px] sm:text-[9px] text-[#1e3a5f]">Margin</p>
                      <p className="text-xs sm:text-sm font-bold text-[#2d5a8e]">18.5%</p>
                    </div>
                    <div className="p-1.5 sm:p-2 rounded-lg bg-blue-50 text-center">
                      <p className="text-[8px] sm:text-[9px] text-blue-600">Runway</p>
                      <p className="text-xs sm:text-sm font-bold text-blue-700">4.2 mo</p>
                    </div>
                    <div className="p-1.5 sm:p-2 rounded-lg bg-amber-50 text-center">
                      <p className="text-[8px] sm:text-[9px] text-amber-600">Tax Saved</p>
                      <p className="text-xs sm:text-sm font-bold text-amber-700">$12,450</p>
                    </div>
                    <div className="p-1.5 sm:p-2 rounded-lg bg-emerald-50 text-center">
                      <p className="text-[8px] sm:text-[9px] text-emerald-600">Reserve</p>
                      <p className="text-xs sm:text-sm font-bold text-emerald-700">$28,000</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#1e3a5f]/10 text-[#2d5a8e]">Features</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">Everything You Need to Succeed</h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              A complete operating system for smart business financial management.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 12V7H5a2 2 0 010-4h14v4"/><path d="M3 5v14a2 2 0 002 2h16v-5"/><path d="M18 12a2 2 0 100 4 2 2 0 000-4z"/></svg>, title: "5 Smart Accounts", desc: "Income, Profit, Owner Pay, Tax, and OpEx accounts with automatic allocation calculations." },
              { icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/></svg>, title: "Bank Connection", desc: "Connect your bank accounts via Plaid for real-time balances and transactions." },
              { icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>, title: "Financial Health Score", desc: "Track your business health with a 0-100 score and actionable recommendations." },
              { icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/></svg>, title: "Tax Planning", desc: "Strategic tax planning with quarterly estimates and entity type comparison." },
              { icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>, title: "475+ Deductions", desc: "Comprehensive deduction library with IRS rules, filterable by business type." },
              { icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21.21 15.89A10 10 0 118 2.83"/><path d="M22 12A10 10 0 0012 2v10z"/></svg>, title: "CPA Reports", desc: "Export professional PDF and CSV reports to share with your accountant." },
              { icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>, title: "Smart Reminders", desc: "Email and SMS reminders on the 10th and 25th to never miss an allocation." },
              { icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>, title: "Mobile Ready", desc: "Access your financial dashboard anywhere with our mobile-responsive design." },
              { icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>, title: "Bank-Level Security", desc: "Your data is protected with enterprise-grade encryption and security." }
            ].map((feature) => (
              <Card key={feature.title} className="card-hover border-stone-200">
                <CardContent className="pt-6">
                  <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center text-[#1e3a5f] mb-4">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">{feature.title}</h3>
                  <p className="text-stone-600 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#1e3a5f]/10 text-[#2d5a8e]">How It Works</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">From Setup to Savings in Minutes</h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Opturna Tax&#8482; handles the complexity so you can focus on growing your business.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-20">
            {/* Connect */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-stone-900 mb-3">Connect Your Business</h3>
                <p className="text-stone-600 mb-4">
                  Select your industry and Opturna auto-configures your expense categories, tax rules, and likely deductions. No manual setup — we support 20+ industries out of the box.
                </p>
                <ul className="space-y-2">
                  {["Industry-specific auto-setup in seconds", "20+ industries with pre-built tax categories", "Smart accounts created: Income, Profit, Owner Pay, Tax, OpEx"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <svg className="w-4 h-4 text-[#1e3a5f] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-[#1e3a5f]/5 to-[#2d5a8e]/5 rounded-2xl p-8 border border-[#1e3a5f]/10">
                <div className="space-y-3">
                  {["Restaurant & Food Service", "Real Estate", "E-Commerce", "Consulting", "Construction"].map((ind, i) => (
                    <div key={i} className={`flex items-center gap-3 px-4 py-3 rounded-xl ${i === 0 ? 'bg-[#1e3a5f] text-white shadow-lg' : 'bg-white text-stone-700 border border-stone-200'}`}>
                      <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-white' : 'bg-stone-300'}`}></div>
                      <span className="text-sm font-medium">{ind}</span>
                      {i === 0 && <span className="ml-auto text-xs opacity-80">Auto-configured</span>}
                    </div>
                  ))}
                  <p className="text-xs text-stone-400 text-center pt-1">+ 15 more industries</p>
                </div>
              </div>
            </div>

            {/* Bookkeeping */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-200/50">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-stone-200">
                    <div>
                      <p className="text-xs text-stone-500">Starbucks - Office Meeting</p>
                      <p className="text-sm font-semibold text-stone-900">-$42.50</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-700 text-xs font-medium">Meals & Entertainment</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-stone-200">
                    <div>
                      <p className="text-xs text-stone-500">Adobe Creative Cloud</p>
                      <p className="text-sm font-semibold text-stone-900">-$54.99</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-lg bg-blue-100 text-blue-700 text-xs font-medium">Software & Tools</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-stone-200">
                    <div>
                      <p className="text-xs text-stone-500">Home Depot - Materials</p>
                      <p className="text-sm font-semibold text-stone-900">-$189.00</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-lg bg-green-100 text-green-700 text-xs font-medium">Supplies & Materials</span>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <h3 className="text-2xl font-bold text-stone-900 mb-3">Automated Bookkeeping & Categorization</h3>
                <p className="text-stone-600 mb-4">
                  Transactions are automatically categorized using custom rules you set or ones Opturna learns from your industry. Snap a receipt and our AI reads it instantly.
                </p>
                <ul className="space-y-2">
                  {["Custom categorization rules that run automatically", "Receipt OCR — snap a photo, AI extracts the details", "Transaction matching catches potential deductions you'd miss"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <svg className="w-4 h-4 text-[#1e3a5f] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Tax Planning */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-stone-900 mb-3">Tax Planning That Works Year-Round</h3>
                <p className="text-stone-600 mb-4">
                  Stop scrambling in April. Opturna calculates estimated quarterly taxes, surfaces deductions you're missing, and gives you a real-time picture of your tax liability.
                </p>
                <ul className="space-y-2">
                  {["Quarterly estimated tax calculations by entity type", "475+ deduction library filtered to your business", "Auto-detected deductions from your transaction history", "Pay quarterly taxes through IRS Direct Pay — we'll redirect you to the IRS site"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <svg className="w-4 h-4 text-[#1e3a5f] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200/50">
                <div className="text-center mb-4">
                  <p className="text-xs text-stone-500 mb-1">Estimated Tax Savings This Year</p>
                  <p className="text-4xl font-bold text-green-700">$12,450</p>
                </div>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-600">Home Office Deduction</span>
                    <span className="font-semibold text-green-700">$3,200</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-600">Vehicle & Mileage</span>
                    <span className="font-semibold text-green-700">$4,800</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-600">Software & Subscriptions</span>
                    <span className="font-semibold text-green-700">$2,150</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-600">Education & Training</span>
                    <span className="font-semibold text-green-700">$2,300</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-green-200 flex items-center justify-between text-sm">
                    <span className="text-stone-500">3 unclaimed deductions detected</span>
                    <span className="text-[#1e3a5f] font-medium">Review &rarr;</span>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Assistant */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 bg-gradient-to-br from-[#1e3a5f]/5 to-blue-50 rounded-2xl p-8 border border-[#1e3a5f]/10">
                <div className="space-y-3">
                  <div className="p-4 bg-white rounded-xl border border-stone-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center">
                        <span className="text-white text-xs font-bold">AI</span>
                      </div>
                      <span className="text-sm font-medium text-stone-900">Opturna AI Assistant</span>
                    </div>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      Your profit margin is 18.5% — healthy, but your marketing spend is 42% of expenses. Consider diversifying. Also, you have 3 unclaimed deductions worth ~$2,300. Want me to break those down?
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <span className="px-3 py-1.5 rounded-full bg-white border border-stone-200 text-xs text-stone-600">How's my business health?</span>
                    <span className="px-3 py-1.5 rounded-full bg-white border border-stone-200 text-xs text-stone-600">What should I ask my CPA?</span>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <h3 className="text-2xl font-bold text-stone-900 mb-3">Your AI Financial Assistant</h3>
                <p className="text-stone-600 mb-4">
                  Ask questions in plain English and get answers based on your actual numbers. Business health checks, CPA prep questions, and deduction insights — always available.
                </p>
                <ul className="space-y-2">
                  {["Real-time business health scoring with actionable advice", "Generates CPA-ready questions based on your financials", "Knows your P&L, cash flow, and deduction history"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <svg className="w-4 h-4 text-[#1e3a5f] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Profit-First */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-stone-900 mb-3">Profit-First Allocation & Cash Flow</h3>
                <p className="text-stone-600 mb-4">
                  Every dollar gets a job. On the 10th and 25th, allocate income across your 5 smart accounts. Watch your financial health score climb as you build reserves and take profit.
                </p>
                <ul className="space-y-2">
                  {["5 smart accounts keep your money organized automatically", "Cash flow projections show you months ahead", "Quarterly reviews and profit distributions on schedule"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <svg className="w-4 h-4 text-[#1e3a5f] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8 border border-violet-200/50">
                <p className="text-xs text-stone-500 mb-4 text-center">Financial Health Score</p>
                <div className="text-center mb-6">
                  <span className="text-5xl font-bold text-[#1e3a5f]">87</span>
                  <span className="text-lg text-stone-400">/100</span>
                </div>
                <div className="space-y-3">
                  {[
                    { label: "Income", amount: "$24,500", color: "bg-[#1e3a5f]", width: "100%" },
                    { label: "Profit (15%)", amount: "$3,675", color: "bg-green-500", width: "15%" },
                    { label: "Owner Pay (50%)", amount: "$12,250", color: "bg-blue-500", width: "50%" },
                    { label: "Tax (15%)", amount: "$3,675", color: "bg-amber-500", width: "15%" },
                    { label: "OpEx (20%)", amount: "$4,900", color: "bg-violet-500", width: "20%" },
                  ].map((acc, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-stone-600">{acc.label}</span>
                        <span className="font-semibold text-stone-900">{acc.amount}</span>
                      </div>
                      <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div className={`h-full ${acc.color} rounded-full`} style={{width: acc.width}}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom quote */}
          <div className="mt-20 p-6 rounded-2xl bg-gradient-to-r from-[#1e3a5f]/5 to-[#2d5a8e]/5 border border-[#1e3a5f]/20 text-center">
            <p className="text-lg text-[#1e3a5f] font-medium">
              "Most businesses overpay in taxes because they don't plan ahead. Opturna changes that."
            </p>
          </div>

          {/* CTA after how it works */}
          <div className="mt-12 text-center">
            <Button
              onClick={() => navigate("/auth")}
              className="bg-[#1e3a5f] hover:bg-[#2d5a8e] text-white rounded-full px-10 py-6 text-lg shadow-lg shadow-[#1e3a5f]/20"
            >
              Start Your Free Trial
            </Button>
            <p className="text-sm text-stone-500 mt-3">No credit card required. Set up in under 2 minutes.</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 bg-gradient-to-br from-stone-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#1e3a5f]/10 text-[#2d5a8e]">Testimonials</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">Founders Who Stopped Guessing</h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Real startup founders who switched from spreadsheets and chaos to Opturna.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                name: "Jake Moretti",
                title: "Co-founder, Stackline (SaaS, Series A)",
                photo: "/testimonials/marcus.jpg",
                quote: "We were burning cash and had no idea our tax bill was going to be $47K. Opturna flagged it in Q2 and helped us set aside the right amount. That alone saved us from a cash crunch that could've killed our runway.",
                metric: "$47K tax bill caught"
              },
              {
                name: "Priya Nair",
                title: "Founder & CEO, BoldPath Studio",
                photo: "/testimonials/jennifer.jpg",
                quote: "I bootstrapped my agency to $600K ARR but I was still using a Google Sheet for finances. Opturna's Profit-First accounts forced me to actually pay myself. First time in 2 years I took a real salary.",
                metric: "First real salary"
              },
              {
                name: "Sam Okafor",
                title: "CTO turned Solo Founder, Loopkey",
                photo: "/testimonials/aisha.jpg",
                quote: "I'm technical, not financial. Opturna's AI assistant literally explains my numbers to me like I'm five. I found $8K in unclaimed deductions I had no clue about — home office, software, even my coworking membership.",
                metric: "$8K in missed deductions"
              },
              {
                name: "Elena Torres",
                title: "Founder, Madera Supply Co. (E-commerce)",
                photo: "/testimonials/david.jpg",
                quote: "Tax season used to mean two weeks of panic and a $3K accountant bill. Now I just export the CPA report and my accountant is done in an hour. He actually asked me what software I was using.",
                metric: "CPA time cut 80%"
              }
            ].map((t) => (
              <Card key={t.name} className="border-stone-200 hover:shadow-lg transition-shadow" data-testid={`testimonial-${t.name.toLowerCase().replace(/\s/g, '-')}`}>
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-start gap-4">
                    <img
                      src={t.photo}
                      alt={t.name}
                      className="w-14 h-14 rounded-full object-cover flex-shrink-0 border-2 border-[#1e3a5f]/10"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <p className="font-semibold text-stone-900 text-sm">{t.name}</p>
                          <p className="text-xs text-stone-500">{t.title}</p>
                        </div>
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">{t.metric}</Badge>
                      </div>
                      <div className="flex gap-0.5 my-2">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <p className="text-stone-600 text-sm leading-relaxed">"{t.quote}"</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gradient-to-br from-stone-50 to-[#1e3a5f]/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#1e3a5f]/10 text-[#2d5a8e]">Pricing</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">Choose Your Plan</h2>
            <p className="text-lg text-stone-600 mb-8">
              Start with a 7-day free trial. No credit card required.
            </p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-stone-900' : 'text-stone-500'}`}>Monthly</span>
              <Switch
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
                className="data-[state=checked]:bg-[#1e3a5f]"
              />
              <span className={`text-sm font-medium ${isAnnual ? 'text-stone-900' : 'text-stone-500'}`}>
                Annual
              </span>
              <Badge className="bg-[#1e3a5f] text-white">Save 2 months</Badge>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tiers.map((tier) => {
              const price = isAnnual ? tier.annualPrice : tier.monthlyPrice;
              const savings = calculateSavings(tier.monthlyPrice, tier.annualPrice);
              
              return (
                <Card 
                  key={tier.key} 
                  className={`relative ${tier.highlighted ? 'border-2 border-[#1e3a5f] shadow-xl scale-105' : tier.key === 'wealth_builder' ? 'border-2 border-emerald-500 shadow-xl' : 'border-stone-200'}`}
                >
                  {tier.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className={tier.highlighted ? "bg-[#1e3a5f]" : tier.key === 'wealth_builder' ? "bg-emerald-600" : "bg-stone-600"}>
                        {tier.badge}
                      </Badge>
                    </div>
                  )}
                  <CardContent className="pt-8 pb-8">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-stone-900 mb-2">{tier.name}</h3>
                      <p className="text-stone-500 text-sm mb-4">{tier.description}</p>
                      <div>
                        <span className="text-4xl font-bold text-stone-900">${price}</span>
                        <span className="text-stone-500">/{isAnnual ? 'year' : 'month'}</span>
                      </div>
                      {isAnnual && (
                        <p className="text-sm text-[#1e3a5f] font-medium mt-2">
                          Save ${savings}/year
                        </p>
                      )}
                    </div>
                    
                    <ul className="space-y-3 mb-8">
                      {tier.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-[#1e3a5f] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-stone-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button
                      onClick={() => navigate("/auth", { state: { tier: tier.key, billing: isAnnual ? 'annual' : 'monthly' } })}
                      className={`w-full py-6 text-lg rounded-full ${
                        tier.highlighted 
                          ? 'bg-[#1e3a5f] hover:bg-[#2d5a8e] text-white' 
                          : tier.key === 'wealth_builder'
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'bg-stone-100 hover:bg-stone-200 text-stone-900'
                      }`}
                      data-testid={`pricing-cta-${tier.key}`}
                    >
                      Start Free Trial
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <p className="text-center text-stone-500 mt-8">
            All plans include a 7-day free trial. Cancel anytime.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-0 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[500px]">
          {/* Left: Photo */}
          <div className="relative min-h-[300px] sm:min-h-[400px]">
            <img src="/cta-person.jpg" alt="Entrepreneur using Opturna" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a1628]/40" />
          </div>
          {/* Right: CTA content */}
          <div className="bg-gradient-to-br from-[#0a1628] via-[#1e3a5f] to-[#0d4a3a] flex items-center justify-center px-8 py-20 lg:py-0">
            <div className="max-w-md text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-400/15 text-emerald-300 text-xs font-medium mb-6 border border-emerald-400/20">
                Limited time: 2 months free on annual plans
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
                Ready to Take Control of Your
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300"> Finances?</span>
              </h2>
              <p className="text-stone-300 mb-8 leading-relaxed">
                Join thousands of business owners using Opturna Tax&#8482; to automate bookkeeping, maximize deductions, and build real wealth.
              </p>
              <Button
                onClick={() => navigate("/auth")}
                className="bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-600 hover:to-teal-500 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-emerald-500/25 border-0 w-full sm:w-auto"
              >
                Start Your 7-Day Free Trial
              </Button>
              <p className="text-xs text-stone-400 mt-4">No credit card required. Set up in under 2 minutes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-[#0a1628] text-stone-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            <div>
              <img src="/owl-logo.png" alt="Opturna Tax" className="h-20 mx-auto mb-4" />
              <p className="text-sm text-stone-500 leading-relaxed">Your all-in-one business financial command center. Built for entrepreneurs who want to keep more of what they earn.</p>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-3">Product</p>
              <div className="space-y-2">
                <a href="#features" className="block text-sm hover:text-emerald-400 transition-colors">Features</a>
                <a href="#pricing" className="block text-sm hover:text-emerald-400 transition-colors">Pricing</a>
                <a href="#how-it-works" className="block text-sm hover:text-emerald-400 transition-colors">How It Works</a>
                <a href="/demo" className="block text-sm hover:text-emerald-400 transition-colors">Interactive Demo</a>
              </div>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-3">Legal</p>
              <div className="space-y-2">
                <a href="/privacy" className="block text-sm hover:text-emerald-400 transition-colors">Privacy Policy</a>
                <a href="/terms" className="block text-sm hover:text-emerald-400 transition-colors">Terms of Service</a>
                <a href="/data-retention" className="block text-sm hover:text-emerald-400 transition-colors">Data Retention</a>
                <a href="/security" className="block text-sm hover:text-emerald-400 transition-colors">Security</a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <span className="text-xs text-stone-500">&copy; 2026 Opturna Tax&#8482;. All rights reserved.</span>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
              <span className="text-xs text-stone-500">All systems operational</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
